import {
  createElement,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import type { AuthUserType } from "../../../redux/actions/authUserActions";
import type { StoreState } from "../../../redux/store";
import {
  getOnlineOrders,
  type OnlineOrderSummary,
} from "../../../services/onlineOrder.service";

const ONLINE_ORDER_POLL_MS = 3 * 60 * 1000;
const ONLINE_ORDERS_ROUTE = "/onlineOrders";
const LAST_SEEN_ORDER_KEY = "online-order-last-seen-id";
const NOTIFIED_ORDER_IDS_SUFFIX = "online-order-notified-ids";
const ALERT_DURATION_SEC = 3;
const MAX_NOTIFIED_IDS_STORED = 500;

interface OnlineOrderNotificationContextValue {
  unreadCount: number;
  refreshUnreadOrders: () => Promise<void>;
}

type BrowserWindow = Window &
  typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };

const OnlineOrderNotificationContext =
  createContext<OnlineOrderNotificationContextValue | null>(null);

const getStorageKey = (authUser: AuthUserType | null) => {
  const userId = authUser?.userId ?? "guest";
  const branchId = authUser?.branchId ?? "all";
  return `${LAST_SEEN_ORDER_KEY}:${userId}:${branchId}`;
};

const getNotifiedIdsKey = (storageKey: string) =>
  storageKey.replace(LAST_SEEN_ORDER_KEY, NOTIFIED_ORDER_IDS_SUFFIX);

const getLastSeenOrderId = (storageKey: string) => {
  if (typeof window === "undefined") {
    return 0;
  }

  const rawValue = window.localStorage.getItem(storageKey);
  const numericValue = Number(rawValue);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
};

const setLastSeenOrderId = (storageKey: string, orderId: number) => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(storageKey, String(orderId));
};

const loadNotifiedOrderIds = (storageKey: string): Set<number> => {
  if (typeof window === "undefined") {
    return new Set();
  }

  try {
    const raw = window.localStorage.getItem(getNotifiedIdsKey(storageKey));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    );
  } catch {
    return new Set();
  }
};

const saveNotifiedOrderIds = (storageKey: string, ids: Set<number>) => {
  if (typeof window === "undefined") {
    return;
  }

  const sorted = Array.from(ids).sort((a, b) => b - a);
  const trimmed = sorted.slice(0, MAX_NOTIFIED_IDS_STORED);
  window.localStorage.setItem(
    getNotifiedIdsKey(storageKey),
    JSON.stringify(trimmed),
  );
};

const getHighestOrderId = (orders: OnlineOrderSummary[]) =>
  orders.reduce((highestId, order) => {
    const orderId = Number(order.id);
    return Number.isFinite(orderId) && orderId > highestId ? orderId : highestId;
  }, 0);

/**
 * Single ~3s retail alert: soft layered tone with slow decay (one playback, not a multi-bell sequence).
 */
const playOnlineOrderAlert = (audioContext: AudioContext) => {
  const t0 = audioContext.currentTime + 0.02;
  const duration = ALERT_DURATION_SEC;

  const master = audioContext.createGain();
  master.connect(audioContext.destination);
  master.gain.setValueAtTime(0.0001, t0);
  master.gain.exponentialRampToValueAtTime(0.38, t0 + 0.06);
  master.gain.exponentialRampToValueAtTime(0.22, t0 + 0.9);
  master.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

  const body = audioContext.createOscillator();
  body.type = "sine";
  body.frequency.setValueAtTime(523.25, t0);
  body.frequency.exponentialRampToValueAtTime(493.88, t0 + duration);
  body.connect(master);
  body.start(t0);
  body.stop(t0 + duration + 0.05);

  const warmth = audioContext.createOscillator();
  warmth.type = "triangle";
  warmth.frequency.setValueAtTime(1046.5, t0);
  const warmthGain = audioContext.createGain();
  warmthGain.gain.setValueAtTime(0.07, t0);
  warmthGain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration * 0.85);
  warmth.connect(warmthGain);
  warmthGain.connect(audioContext.destination);
  warmth.start(t0);
  warmth.stop(t0 + duration + 0.05);
};

const useOnlineOrderNotificationsInternal = (): OnlineOrderNotificationContextValue => {
  const location = useLocation();
  const authUser = useSelector<StoreState, AuthUserType | null>(
    (state) => state.authUser,
  );
  const storageKey = useMemo(() => getStorageKey(authUser), [authUser]);

  const [unreadCount, setUnreadCount] = useState(0);

  const audioContextRef = useRef<AudioContext | null>(null);
  const latestOrdersRef = useRef<OnlineOrderSummary[]>([]);
  const notifiedOrderIdsRef = useRef<Set<number>>(new Set());
  const hasLoadedRef = useRef(false);
  const pollInFlightRef = useRef(false);
  const isPlayingRef = useRef(false);

  const primeAudioContext = useCallback(async () => {
    if (typeof window === "undefined") {
      return null;
    }

    const browserWindow = window as BrowserWindow;
    const AudioContextConstructor =
      window.AudioContext ?? browserWindow.webkitAudioContext;

    if (!AudioContextConstructor) {
      return null;
    }

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextConstructor();
    }

    if (audioContextRef.current.state === "suspended") {
      try {
        await audioContextRef.current.resume();
      } catch {
        return audioContextRef.current;
      }
    }

    return audioContextRef.current;
  }, []);

  const playOrderAlert = useCallback(async () => {
    if (isPlayingRef.current) {
      return;
    }

    const audioContext = await primeAudioContext();
    if (!audioContext || audioContext.state !== "running") {
      return;
    }

    isPlayingRef.current = true;
    try {
      playOnlineOrderAlert(audioContext);
    } finally {
      window.setTimeout(() => {
        isPlayingRef.current = false;
      }, ALERT_DURATION_SEC * 1000 + 80);
    }
  }, [primeAudioContext]);

  const syncUnreadCount = useCallback(
    (orders: OnlineOrderSummary[]) => {
      latestOrdersRef.current = orders;

      const highestOrderId = getHighestOrderId(orders);
      const isOnOnlineOrdersPage = location.pathname === ONLINE_ORDERS_ROUTE;
      let lastSeenOrderId = getLastSeenOrderId(storageKey);

      if (isOnOnlineOrdersPage && highestOrderId > lastSeenOrderId) {
        lastSeenOrderId = highestOrderId;
        setLastSeenOrderId(storageKey, highestOrderId);
      }

      const nextUnreadCount = isOnOnlineOrdersPage
        ? 0
        : orders.filter((order) => Number(order.id) > lastSeenOrderId).length;

      setUnreadCount(nextUnreadCount);
    },
    [location.pathname, storageKey],
  );

  const refreshUnreadOrders = useCallback(async () => {
    if (!authUser?.["auth-token"]) {
      setUnreadCount(0);
      latestOrdersRef.current = [];
      notifiedOrderIdsRef.current = new Set();
      hasLoadedRef.current = false;
      return;
    }

    if (pollInFlightRef.current) {
      return;
    }

    pollInFlightRef.current = true;

    try {
      const response = await getOnlineOrders("new");
      const orders = Array.isArray(response.data) ? response.data : [];

      if (!hasLoadedRef.current) {
        const existing = loadNotifiedOrderIds(storageKey);
        orders.forEach((order) => {
          const orderId = Number(order.id);
          if (Number.isFinite(orderId) && orderId > 0) {
            existing.add(orderId);
          }
        });
        notifiedOrderIdsRef.current = existing;
        saveNotifiedOrderIds(storageKey, existing);
        hasLoadedRef.current = true;
        syncUnreadCount(orders);
        return;
      }

      const notified = notifiedOrderIdsRef.current;
      const newlyArrived = orders.filter((order) => {
        const orderId = Number(order.id);
        return (
          Number.isFinite(orderId) &&
          orderId > 0 &&
          !notified.has(orderId)
        );
      });

      if (newlyArrived.length > 0) {
        newlyArrived.forEach((order) => {
          notified.add(Number(order.id));
        });
        notifiedOrderIdsRef.current = notified;
        saveNotifiedOrderIds(storageKey, notified);
        await playOrderAlert();
      }

      syncUnreadCount(orders);
    } catch {
      // Keep polling silently; notification failures should not interrupt POS usage.
    } finally {
      pollInFlightRef.current = false;
    }
  }, [authUser, playOrderAlert, storageKey, syncUnreadCount]);

  useEffect(() => {
    const unlockAudio = () => {
      void primeAudioContext();
    };

    window.addEventListener("pointerdown", unlockAudio);
    window.addEventListener("keydown", unlockAudio);

    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, [primeAudioContext]);

  useEffect(() => {
    latestOrdersRef.current = [];
    hasLoadedRef.current = false;
    notifiedOrderIdsRef.current = loadNotifiedOrderIds(storageKey);
    setUnreadCount(0);
  }, [storageKey]);

  useEffect(() => {
    void refreshUnreadOrders();

    const intervalId = window.setInterval(() => {
      void refreshUnreadOrders();
    }, ONLINE_ORDER_POLL_MS);

    return () => window.clearInterval(intervalId);
  }, [refreshUnreadOrders]);

  useEffect(() => {
    syncUnreadCount(latestOrdersRef.current);
  }, [location.pathname, syncUnreadCount]);

  return {
    unreadCount,
    refreshUnreadOrders,
  };
};

interface OnlineOrderNotificationProviderProps {
  children: ReactNode;
}

export const OnlineOrderNotificationProvider = ({
  children,
}: OnlineOrderNotificationProviderProps) => {
  const value = useOnlineOrderNotificationsInternal();

  return createElement(
    OnlineOrderNotificationContext.Provider,
    { value },
    children,
  );
};

export const useOnlineOrderNotifications = () => {
  const context = useContext(OnlineOrderNotificationContext);

  if (!context) {
    throw new Error(
      "useOnlineOrderNotifications must be used within OnlineOrderNotificationProvider",
    );
  }

  return context;
};

export default useOnlineOrderNotifications;
